export const TIMEZONE_MAP = {
  UTC: "UTC",
  IST: "Asia/Kolkata",
  EST: "America/New_York",
  PST: "America/Los_Angeles",
  CST: "America/Chicago",
};

/**
 * Converts a local HH:mm time in the given timezone key to UTC {hour, minute}.
 * Uses the Intl API so DST is handled automatically.
 */
export function convertToUTC(time, timezone) {
  const [hour, minute] = time.split(":").map(Number);
  const ianaZone = TIMEZONE_MAP[timezone] || "UTC";

  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");

  // Treat the input time as a LOCAL time in the target timezone.
  // We create a string like "2024-06-01T09:00:00" and ask Intl what
  // UTC instant corresponds to that local time in ianaZone.
  // The trick: format a known UTC instant in the target zone and find
  // what UTC offset applies today in that zone.

  // Step 1: pick an arbitrary UTC instant (noon UTC today)
  const noonUTC = new Date(`${year}-${month}-${day}T12:00:00Z`);

  // Step 2: find what UTC offset is active in ianaZone right now
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: ianaZone,
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    // Also get date parts to handle day boundaries
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(noonUTC);
  const tzHour   = parseInt(parts.find((p) => p.type === "hour").value,   10);
  const tzMinute = parseInt(parts.find((p) => p.type === "minute").value, 10);

  // noonUTC (12:00 UTC) appears as tzHour:tzMinute in the target zone
  // So offset = tzHour:tzMinute - 12:00  (in minutes)
  const offsetMinutes = (tzHour * 60 + tzMinute) - 12 * 60;

  // Step 3: subtract offset to convert local → UTC
  // local = UTC + offset  →  UTC = local - offset
  let utcMinutes = hour * 60 + minute - offsetMinutes;
  utcMinutes = ((utcMinutes % 1440) + 1440) % 1440;

  return {
    hour:   Math.floor(utcMinutes / 60),
    minute: utcMinutes % 60,
  };
}

export function buildCronExpression(frequency, executionTime, timezone) {
  if (frequency === "minutely") return "* * * * *";

  const { hour, minute } = convertToUTC(executionTime, timezone);

  switch (frequency) {
    case "hourly":  return `${minute} * * * *`;
    case "daily":   return `${minute} ${hour} * * *`;
    case "weekly":  return `${minute} ${hour} * * 1`;
    case "monthly": return `${minute} ${hour} 1 * *`;
    default:        return `${minute} ${hour} * * *`;
  }
}